/**
 * Graphes en SVG maison, sans dépendance.
 *
 * C'est la convention déjà en place sur les autres protos (cf. projet-vernon) :
 * une courbe et des barres ne justifient pas 40 ko de librairie, et un SVG
 * qu'on écrit soi-même se restyle en une ligne le jour de la refonte DA.
 */

interface Point {
  day: string;
  value: number | null;
}

export function LineChart({
  points,
  height = 120,
  color = "var(--color-accent)",
  label,
}: {
  points: Point[];
  height?: number;
  color?: string;
  label?: string;
}) {
  const values = points.filter((p): p is { day: string; value: number } => p.value !== null);

  if (values.length < 2) {
    return <EmptyChart height={height} label={label} />;
  }

  const width = 320;
  const pad = 4;
  const min = Math.min(...values.map((p) => p.value));
  const max = Math.max(...values.map((p) => p.value));
  const span = max - min || 1;

  // On projette sur l'index dans la série complète, pas dans la série filtrée :
  // un trou de données doit se voir comme un écart, pas être refermé.
  const xFor = (index: number) => pad + (index / Math.max(1, points.length - 1)) * (width - pad * 2);
  const yFor = (value: number) => height - pad - ((value - min) / span) * (height - pad * 2);

  const path = points
    .map((p, i) => (p.value === null ? null : `${xFor(i)},${yFor(p.value)}`))
    .filter(Boolean)
    .join(" ");

  const last = values.at(-1)!;
  const first = values[0]!;
  const delta = last.value - first.value;

  return (
    <figure className="flex flex-col gap-1">
      {label && (
        <figcaption className="flex items-baseline justify-between text-xs">
          <span className="text-[var(--color-muted)]">{label}</span>
          <span className="tabular-nums">
            {last.value.toLocaleString("fr-FR")}
            <span className={delta >= 0 ? "text-[var(--color-good)]" : "text-[var(--color-bad)]"}>
              {" "}
              {delta >= 0 ? "+" : ""}
              {delta.toLocaleString("fr-FR")}
            </span>
          </span>
        </figcaption>
      )}
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label={label ?? "courbe"}>
        <polyline points={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
      </svg>
    </figure>
  );
}

export function BarChart({
  points,
  height = 80,
  color = "var(--color-accent)",
  label,
}: {
  points: Point[];
  height?: number;
  color?: string;
  label?: string;
}) {
  const values = points.map((p) => p.value ?? 0);
  const max = Math.max(...values, 1);

  if (points.length === 0) return <EmptyChart height={height} label={label} />;

  const width = 320;
  const gap = 1;
  const barWidth = Math.max(1, width / points.length - gap);

  return (
    <figure className="flex flex-col gap-1">
      {label && <figcaption className="text-xs text-[var(--color-muted)]">{label}</figcaption>}
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label={label ?? "barres"}>
        {points.map((p, i) => {
          const value = p.value ?? 0;
          const h = (value / max) * height;
          return (
            <rect
              key={p.day}
              x={i * (barWidth + gap)}
              y={height - h}
              width={barWidth}
              height={h}
              fill={color}
              opacity={value === 0 ? 0.15 : 1}
            />
          );
        })}
      </svg>
    </figure>
  );
}

function EmptyChart({ height, label }: { height: number; label?: string }) {
  return (
    <figure className="flex flex-col gap-1">
      {label && <figcaption className="text-xs text-[var(--color-muted)]">{label}</figcaption>}
      <div
        className="flex items-center justify-center rounded-lg border border-dashed border-[var(--color-line)] text-xs text-[var(--color-muted)]"
        style={{ height }}
      >
        Pas encore assez de données
      </div>
    </figure>
  );
}
